import { Button, FormInput, FormRoot, FormTextarea, RHF, Switch, zodResolver, Field } from "@devhop/ui";
import { z } from "zod";
import type {
  CreateCategoryDTO,
  UpdateCategoryDTO,
} from "@/modules/form-center/domain/contracts/categories";
import { IconPicker } from "../../../../shared/ui/IconPicker";

const CategoryFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຫມວດຫມູ່"),
  description: z.string().optional(),
  icon: z.string().optional(),
  orderIndex: z
    .union([z.number(), z.string()])
    .transform((val) => {
      if (typeof val === "string") {
        const num = Number(val);
        return Number.isNaN(num) ? 0 : num;
      }
      return val;
    })
    .pipe(z.number().int().min(0))
    .default(0),
  isActive: z.boolean().default(true),
});

export type CategoryFormValues = z.input<typeof CategoryFormSchema>;

export function CategoryForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<CategoryFormValues>;
  onSubmit: (values: CreateCategoryDTO | UpdateCategoryDTO) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<CategoryFormValues>({
    resolver: zodResolver(CategoryFormSchema),
    defaultValues: {
      name: "",
      description: "",
      icon: "",
      orderIndex: 0 as string | number,
      isActive: true,
      ...initialValues,
    },
  });

  return (
    <FormRoot
      methods={methods}
      onSubmit={(vals) => {
        const parsed = CategoryFormSchema.parse(vals);
        onSubmit({
          name: parsed.name,
          description: parsed.description || undefined,
          icon: parsed.icon || undefined,
          orderIndex: parsed.orderIndex,
          isActive: parsed.isActive,
        });
      }}
      className="space-y-4"
    >
      <div className="min-w-0 max-w-full" data-tourid="category-form-name">
        <FormInput
          name="name"
          label="ຊື່ຫມວດຫມູ່"
          requiredMark
          placeholder="ຕົວຢ່າງ: ຟອມສະໝັກວຽກ, ຟອມລາຍງານ"
        />
      </div>

      <div className="min-w-0 max-w-full" data-tourid="category-form-description">
        <FormTextarea
          name="description"
          label="ຄຳອະທິບາຍ"
          placeholder="ອະທິບາຍສັ້ນໆ ກ່ຽວກັບຫມວດຫມູ່ນີ້"
          rows={3}
        />
      </div>

      <div className="min-w-0 max-w-full" data-tourid="category-form-icon">
        <Field name="icon" label="ໄອຄອນ (Icon)" hint="ເລືອກໄອຄອນຈາກ lucide-react (ບໍ່ບັງຄັບ)">
          <RHF.Controller
            control={methods.control}
            name="icon"
            render={({ field }) => (
              <IconPicker
                value={field.value || ""}
                onChange={field.onChange}
              />
            )}
          />
        </Field>
      </div>

      <div className="min-w-0 max-w-full" data-tourid="category-form-order">
        <FormInput
          name="orderIndex"
          label="ລໍາດັບການສະແດງ"
          type="number"
          placeholder="0"
          hint="ຕົວເລກທີ່ຕ່ຳຈະສະແດງກ່ອນ"
        />
      </div>

      <div className="flex items-center justify-between rounded-lg border p-4" data-tourid="category-form-active">
        <div>
          <label htmlFor="category-is-active" className="font-medium text-sm">ສະຖານະ</label>
          <p className="text-xs text-muted-foreground">
            ເປີດ/ປິດການໃຊ້ງານຫມວດຫມູ່ນີ້
          </p>
        </div>
        <Switch
          id="category-is-active"
          checked={methods.watch("isActive")}
          onCheckedChange={(checked) => methods.setValue("isActive", checked)}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4" data-tourid="category-form-submit">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
